import React from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Chip, Divider, Grid } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { useDispatch, useSelector } from 'react-redux';
import {
  selectPage2,
  selectPage2DefaultValues,
  selectPage2OrderPriorities,
  selectedOrderTypeDetails,
} from '@AMIEWEB/NewOrder/Store/Order.selectors';
import { selectOrderDetailsUIView } from '@AMIEWEB/Order/Store/Order.selectors';
import { IAutoCompleteOption } from '../Common/AutoCompleteRenderChips';
import { missingField } from 'app/constants';
import { RowSection } from '../Common/RowSection';
import { Reference } from 'app/models/Unit/CreateUnit';
import { formatDate, formatReferenceText } from '../helper';
import { orderCreationNewActions } from '@AMIEWEB/NewOrder/Store/OrderCreation.actions';

const useStyles = makeStyles()(theme => ({
  boxInlineHeadLabelTxt: {
    'font-weight': '600',
    'margin-top': '0px',
    fontSize: '16px',
  },

  list: {
    paddingLeft: theme.spacing(3),
    marginTop: 0,
    marginBottom: 0,
    font: 'normal normal normal 14px/19px Roboto',
  },
}));

interface AdditionalRequirementsOneProps {
  isNewOrderCreation: boolean;
}

class AdditionalRequirementsOneFormData {
  orderPrioritiesData: IAutoCompleteOption[] = [];

  reference: Reference = null;

  bestBets: string = missingField;

  okToTravel: string;

  strikeDate: string;

  orderType: string;
}

const AdditionalRequirementsOne = (props: AdditionalRequirementsOneProps) => {
  const { isNewOrderCreation } = props;
  const { t } = useTranslation();
  const { classes } = useStyles();
  const orderPrioritiesData = useSelector(selectPage2OrderPriorities);
  const page2Data = useSelector(selectPage2);
  const orderDetailsUIView = useSelector(selectOrderDetailsUIView);
  const orderTypeDetail = useSelector(selectedOrderTypeDetails);
  const arePage2DefaultValuesSet = useSelector(selectPage2DefaultValues);
  const dispatch = useDispatch();
  React.useEffect(() => {
    if (!arePage2DefaultValuesSet) dispatch(orderCreationNewActions.setDefaultValuesPage2());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const displayData = new AdditionalRequirementsOneFormData();

  if (isNewOrderCreation) {
    displayData.orderPrioritiesData = orderPrioritiesData;
    displayData.bestBets = page2Data?.bestBets;
    displayData.reference =  page2Data?.reference;
    displayData.okToTravel = page2Data?.orderStrike?.okToTravel
      ? `${t('order.orderDetails.buttonConfirmation.Yes')}`
      : `${t('order.orderDetails.buttonConfirmation.No')}`;
    displayData.strikeDate = formatDate(page2Data?.orderStrike?.strikeNoticeDate);
    displayData.orderType = orderTypeDetail?.orderType;
  } else {
    displayData.orderPrioritiesData = orderDetailsUIView?.orderDetails?.orderPriorities
      ? orderDetailsUIView?.orderDetails?.orderPriorities?.map<IAutoCompleteOption>(x => {
          return {
            name: x?.description,
            value: `${x?.orderPriorityTypeId}`,
            tooltip: x?.description,
            hideDeleteIcon: false,
          };
        })
      : [];
    displayData.bestBets = orderDetailsUIView?.orderDetails?.bestBets ?? missingField;
    displayData.reference = orderDetailsUIView?.orderDetails?.reference;
    displayData.okToTravel = orderDetailsUIView?.orderDetails?.orderStrike?.oKtoTravel
      ? `${t('order.orderDetails.buttonConfirmation.Yes')}`
      : `${t('order.orderDetails.buttonConfirmation.No')}`;
    displayData.strikeDate = formatDate(orderDetailsUIView?.orderDetails?.orderStrike?.strikeNoticeDate);
    displayData.orderType = orderDetailsUIView?.orderDetails?.orderType;
  }

  return (
    <Grid sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
      <Grid item direction="column" width="100%">
        {displayData?.orderType === 'Strike' && (
          <>
            <RowSection
              header={<div className={classes.boxInlineHeadLabelTxt}>{t('order.newOrderCreation.tabs.strike')}</div>}
              data={``}
            />
            <RowSection
              header={t('order.newOrderCreation.orderDetails.strikeDetails.okToTravel')}
              data={displayData?.okToTravel}
            />
            <RowSection
              header={t('order.newOrderCreation.orderDetails.strikeDetails.strikeNoticeDate')}
              data={displayData?.strikeDate}
            />

            <Divider style={{ marginBottom: '12px' }} />
          </>
        )}

        <RowSection
          header={
            <div className={classes.boxInlineHeadLabelTxt}>{t('order.newOrderCreation.tabs.orderPriorities')}</div>
          }
          data={``}
        />

        <Box marginBottom={'6px'}>
          {displayData?.orderPrioritiesData?.map((data, index) => (
            <ul key={index} className={classes.list}>
              <li key={index}> {data.name} </li>
            </ul>
          ))}
        </Box>
        <Divider style={{ marginBottom: '12px' }} />
        <RowSection
          header={<div className={classes.boxInlineHeadLabelTxt}>{t('order.newOrderCreation.tabs.references')}</div>}
          data={``}
        />
        {!displayData?.reference ? (
          missingField
        ) : (
          <Box>
            <RowSection
              header={t('facility.references.referencesRequired')}
              data={formatReferenceText(displayData?.reference).referencesRequired}
            />
            <RowSection
              header={t('facility.references.referenceToSubmit')}
              data={formatReferenceText(displayData?.reference).referenceToSubmit}
            />
            {displayData?.reference?.referenceFromSupervisor && (
              <Chip label={t('facility.units.unitDetails.additionalInformation.references.supervisor')} />
            )}
          </Box>
        )}

        <Divider sx={{ marginBottom: '12px', marginTop: '12px' }} />
        <RowSection
          header={<div className={classes.boxInlineHeadLabelTxt}>{t('order.newOrderCreation.tabs.bestBets')}</div>}
          data={``}
        />
        <RowSection header={t('order.newOrderCreation.tabs.bestBets')} data={displayData?.bestBets} />
      </Grid>
    </Grid>
  );
};

export default AdditionalRequirementsOne;
